import React, { useState, useEffect, useRef } from 'react';
import { Icon, Button, Modal, Form, Dropdown, Input, Label, Message, Checkbox } from 'semantic-ui-react';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import _ from 'lodash';
import { post } from '../util/api';

const LANGUAGE_OPTIONS = [
	{ key: 'tr', text: 'Turkish (tr)', value: 'tr' },
	{ key: 'en', text: 'English (en)', value: 'en' },
	{ key: 'en_US', text: 'English (US)', value: 'en_US' },
	{ key: 'de', text: 'German (de)', value: 'de' },
	{ key: 'fr', text: 'French (fr)', value: 'fr' },
	{ key: 'es', text: 'Spanish (es)', value: 'es' },
	{ key: 'ar', text: 'Arabic (ar)', value: 'ar' },
	{ key: 'ru', text: 'Russian (ru)', value: 'ru' },
];

const BUTTON_TYPES = [
	{ key: 'qr', text: 'Quick reply', value: 'QUICK_REPLY' },
	{ key: 'url', text: 'URL', value: 'URL' },
	{ key: 'phone', text: 'Phone number', value: 'PHONE_NUMBER' },
	{ key: 'copy', text: 'Copy code (kupon)', value: 'COPY_CODE' },
	{ key: 'catalog', text: 'Catalog', value: 'CATALOG' },
	{ key: 'mpm', text: 'Multi-product (MPM)', value: 'MPM' },
	{ key: 'flow', text: 'Flow', value: 'FLOW' },
];

// WhatsApp/360 metin uzunluk sınırları (karakter sayaçları için)
const LIMITS = { header: 60, body: 1024, footer: 60 };
// Body biçimlendirme araç çubuğu (WhatsApp markdown sözdizimi)
const FORMATS = [
	{ icon: 'bold', marker: '*', title: 'Kalın' },
	{ icon: 'italic', marker: '_', title: 'İtalik' },
	{ icon: 'strikethrough', marker: '~', title: 'Üstü çizili' },
	{ icon: 'code', marker: '```', title: 'Monospace' },
];
const EMOJIS = ['😀', '😉', '😍', '👍', '🙏', '🎉', '🔥', '✅', '❌', '⭐', '❤️', '📦', '🛒', '💰', '⏰', '📢'];
// {{1}} (pozisyonel) veya {{isim}} (named) değişkenleri; token'ı yakalar
const VAR_RE = /{{\s*([A-Za-z0-9_]+)\s*}}/g;
function extractVars(text) {
	const out = [];
	let m;
	VAR_RE.lastIndex = 0;
	while ((m = VAR_RE.exec(text || ''))) if (!out.includes(m[1])) out.push(m[1]);
	return out;
}
const isNamedToken = (tok) => !/^\d+$/.test(tok);

const styles = {
	sectionHeader: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' },
	buttonField: { marginBottom: '12px', padding: '12px', border: '1px solid #e3e6ea', borderRadius: '6px', background: '#fafbfc' },
	// Form içindeki mantıksal bölümler (header/body/footer/buttons) için kart kutusu
	section: { padding: '14px 16px', border: '1px solid #e3e6ea', borderRadius: '8px', marginBottom: '14px', background: '#fff' },
	card: { padding: '12px', border: '1px solid #e3e6ea', borderRadius: '8px', marginBottom: '12px', background: '#fafbfc' },
	cardHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 },
	thumb: { width: '100%', maxHeight: 120, objectFit: 'cover', borderRadius: 6, marginBottom: 8, border: '1px solid #eee', background: '#f4f4f4' },
	thumbPlaceholder: { width: '100%', height: 90, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#c3c8ce', borderRadius: 6, marginBottom: 8, border: '1px dashed #d5d9de', background: '#f7f8f9' },
	hint: { fontSize: 11, color: '#9299a1', marginTop: 3 },
	// --- WhatsApp benzeri önizleme ---
	phone: { border: '1px solid #d9dde1', borderRadius: 18, padding: 10, background: '#e5ddd5', position: 'sticky', top: 0, boxShadow: '0 1px 4px rgba(0,0,0,.08)' },
	bubble: { background: '#fff', borderRadius: '0 8px 8px 8px', padding: 8, boxShadow: '0 1px 1px rgba(0,0,0,.12)', wordBreak: 'break-word', maxWidth: '100%' },
	previewMedia: { width: '100%', maxHeight: 140, objectFit: 'cover', borderRadius: 6, display: 'block', marginBottom: 6, background: '#f0f0f0' },
	previewMediaPh: { width: '100%', height: 110, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#b7bcc2', borderRadius: 6, marginBottom: 6, background: '#f0f0f0' },
	previewBody: { whiteSpace: 'pre-wrap', fontSize: 13, color: '#111', padding: '2px 2px 4px' },
	previewFooter: { fontSize: 11, color: '#8a8f95', marginTop: 4 },
	previewButton: { color: '#00a5f4', padding: '7px 10px', textAlign: 'center', marginTop: 4, fontWeight: 500, fontSize: 13, borderTop: '1px solid #eef0f2' },
	// yatay kaydırmalı carousel önizleme şeridi
	carouselStrip: { display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 6, marginTop: 8 },
	carouselItem: { flex: '0 0 150px', width: 150, background: '#fff', borderRadius: 8, overflow: 'hidden', boxShadow: '0 1px 2px rgba(0,0,0,.12)' },
};

const defaultState = () => ({
	name: '',
	language: 'tr',
	category: 'MARKETING',
	allow_category_change: false,
	templateKind: 'STANDARD', // STANDARD | CAROUSEL_PRODUCT | CAROUSEL_MEDIA
	components: {
		header: { enabled: false, format: 'TEXT', text: '', url: '', example: '' },
		body: { enabled: true, text: '' },
		footer: { enabled: false, text: '' },
		buttons: [],
		// Limited Time Offer (kampanya/geri sayım) — sadece MARKETING
		lto: { enabled: false, text: '', has_expiration: false },
		// Sesli arama izni isteği
		callPermission: { enabled: false },
		variables: {},
		carousel: {
			cardCount: 2, // ürün carousel
			buttonText: 'View',
			cardFormat: 'IMAGE', // medya carousel
			cards: [{ url: '', body: '', buttons: [] }, { url: '', body: '', buttons: [] }],
		},
		auth: {
			add_security_recommendation: true,
			code_expiration_minutes: 5,
			otp_type: 'copy_code', // copy_code | one_tap | zero_tap
			button_text: '',
			autofill_text: '',
			package_name: '',
			signature_hash: '',
		},
	},
});

function newButton() {
	return { type: 'QUICK_REPLY', text: '', value: '', example: '', flow_id: '', flow_action: 'navigate', navigate_screen: '' };
}

// Projedeki kalıcı CDN upload'ını kullanan URL girişi (elle URL de yazılabilir, dosya da yüklenebilir).
// Modül seviyesinde tanımlı → her render'da remount olmaz, focus korunur.
function MediaInput({ value, onChange, accept = 'image/*', placeholder }) {
	const [uploading, setUploading] = useState(false);
	const fileRef = useRef(null);
	async function onFile(e) {
		// event pooling: input referansını await öncesi yakala (e sonradan null'lanır)
		const input = e.target;
		const file = input.files[0];
		if (!file) return;
		setUploading(true);
		try {
			const fd = new FormData();
			fd.append('files[]', file);
			const resp = await fetch('https://uploads.yapaytech.com/flow/uploadPermImage', { method: 'POST', body: fd });
			const json = await resp.json();
			const url = json && json.files && json.files[0] && json.files[0].url;
			if (url) onChange(url);
		} catch (err) {
			console.error('upload error', err);
		} finally {
			setUploading(false);
			if (input) input.value = '';
		}
	}
	return (
		<Input action fluid>
			<input value={value || ''} placeholder={placeholder || 'https://...'} onChange={(e) => onChange(e.target.value)} />
			<Button type="button" icon={uploading ? undefined : 'upload'} loading={uploading} disabled={uploading} onClick={() => fileRef.current && fileRef.current.click()} />
			<input ref={fileRef} type="file" accept={accept} style={{ display: 'none' }} onChange={onFile} />
		</Input>
	);
}

function acceptFor(format) {
	const f = (format || '').toUpperCase();
	if (f === 'VIDEO') return 'video/mp4,video/3gpp';
	if (f === 'DOCUMENT') return 'application/pdf,.pdf';
	// WhatsApp image header yalnızca JPEG/PNG kabul eder (SVG/GIF/WebP desteklenmez)
	return 'image/jpeg,image/png';
}

function isSvg(url) {
	return /\.svg(\?|#|$)/i.test(url || '');
}

function CreateDialog360Template({ modalOpen, onClose, onTemplateCreated, waba_gen_id, t }) {
	const [data, setData] = useState(defaultState());
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState('');

	useEffect(() => {
		if (modalOpen) {
			setData(defaultState());
			setError('');
		}
	}, [modalOpen]);

	const isAuth = data.category === 'AUTHENTICATION';
	const kind = isAuth ? 'AUTH' : data.templateKind;

	const [namedInput, setNamedInput] = useState('');
	const bodyRef = useRef(null);

	const setComp = (name, patch) => setData((prev) => ({ ...prev, components: { ...prev.components, [name]: { ...prev.components[name], ...patch } } }));

	const bodyVariables = extractVars(data.components.body.text);
	const hasNamedVar = bodyVariables.some(isNamedToken);
	const hasPositionalVar = bodyVariables.some((v) => !isNamedToken(v));

	// --- Body metin araçları (biçimlendirme / emoji / değişken) ---
	// Textarea seçimini işaretleyicilerle sarar (WhatsApp *bold* vb.)
	function applyToBody(mutate) {
		const ta = bodyRef.current;
		const text = data.components.body.text || '';
		const start = ta ? ta.selectionStart : text.length;
		const end = ta ? ta.selectionEnd : start;
		const { text: newText, caret } = mutate(text, start, end);
		setComp('body', { text: newText });
		setTimeout(() => {
			if (!ta) return;
			ta.focus();
			ta.selectionStart = ta.selectionEnd = caret;
		}, 0);
	}
	function wrapSelection(marker) {
		applyToBody((text, start, end) => {
			const sel = text.slice(start, end);
			const next = text.slice(0, start) + marker + sel + marker + text.slice(end);
			return { text: next, caret: end + marker.length * 2 };
		});
	}
	function insertAtCursor(str) {
		applyToBody((text, start, end) => ({ text: text.slice(0, start) + str + text.slice(end), caret: start + str.length }));
	}
	function addVariable() {
		// mevcut en yüksek pozisyonel numaranın bir fazlası
		const nums = bodyVariables.filter((v) => !isNamedToken(v)).map(Number);
		const next = (nums.length ? Math.max(...nums) : 0) + 1;
		insertAtCursor(`{{${next}}}`);
	}
	function addNamedVariable() {
		const name = (namedInput || '').trim().replace(/[^A-Za-z0-9_]/g, '_');
		if (!name || /^\d/.test(name)) return;
		insertAtCursor(`{{${name}}}`);
		setNamedInput('');
	}
	function setVariable(key, value) {
		setData((prev) => ({ ...prev, components: { ...prev.components, variables: { ...prev.components.variables, [key]: value } } }));
	}

	// --- Buton dizisi editörü (hem ana butonlar hem carousel kart butonları) ---
	// NOT: JSX component'i olarak DEĞİL, düz fonksiyon olarak çağrılır; aksi halde her
	// render'da yeni component tipi oluşup input'lar remount olur ve focus kaybolur.
	function renderButtons(buttons, onChange, allowCarousel) {
		const types = allowCarousel ? BUTTON_TYPES.filter((x) => ['QUICK_REPLY', 'URL', 'PHONE_NUMBER'].includes(x.value)) : BUTTON_TYPES;
		const set = (i, patch) => onChange(buttons.map((b, idx) => (idx === i ? { ...b, ...patch } : b)));
		return (
			<div>
				<Button type="button" icon="add" size="tiny" content={t('addButton') || 'Buton Ekle'} disabled={buttons.length >= 10} onClick={() => onChange([...buttons, newButton()])} />
				{buttons.map((b, i) => (
					<div key={i} style={styles.buttonField}>
						<div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
							<Dropdown selection value={b.type} options={types} onChange={(_, { value }) => set(i, { type: value, value: '', example: '' })} />
							<Button type="button" icon="trash" size="tiny" onClick={() => onChange(buttons.filter((_, idx) => idx !== i))} />
						</div>
						{b.type !== 'CATALOG' && b.type !== 'MPM' && b.type !== 'COPY_CODE' && (
							<Form.Input placeholder={t('buttonTitle') || 'Buton başlığı'} value={b.text} onChange={(e) => set(i, { text: e.target.value })} />
						)}
						{b.type === 'URL' && (
							<>
								<Form.Input placeholder={t('enterUrl') || 'https://... ({{1}} olabilir)'} value={b.value} onChange={(e) => set(i, { value: e.target.value })} />
								<div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>
									{t('dynamicUrlHint') || 'Linki gönderimde değiştirmek için sonuna {{1}} ekleyin, ör: https://site.com/{{1}}'}
								</div>
								{/{{\d+}}/.test(b.value || '') && (
									<Form.Input label={t('urlExample') || 'Örnek URL (değişken için)'} placeholder="https://site.com/promo" value={b.example} onChange={(e) => set(i, { example: e.target.value })} />
								)}
							</>
						)}
						{b.type === 'PHONE_NUMBER' && <Form.Input placeholder={t('enterPhoneNumber') || '+90...'} value={b.value} onChange={(e) => set(i, { value: e.target.value })} />}
						{b.type === 'COPY_CODE' && <Form.Input placeholder={t('couponCode') || 'Kupon kodu (ör. INDIRIM20)'} value={b.value} onChange={(e) => set(i, { value: e.target.value })} />}
						{b.type === 'FLOW' && (
							<>
								<Form.Input label="Flow ID" placeholder="123456789" value={b.flow_id} onChange={(e) => set(i, { flow_id: e.target.value })} />
								<Form.Group widths="equal">
									<Form.Field>
										<label>Flow action</label>
										<Dropdown selection value={b.flow_action} options={[{ key: 'nav', text: 'navigate', value: 'navigate' }, { key: 'de', text: 'data_exchange', value: 'data_exchange' }]} onChange={(_, { value }) => set(i, { flow_action: value })} />
									</Form.Field>
									{b.flow_action === 'navigate' && <Form.Input label="Navigate screen" placeholder="SCREEN_NAME" value={b.navigate_screen} onChange={(e) => set(i, { navigate_screen: e.target.value })} />}
								</Form.Group>
							</>
						)}
					</div>
				))}
			</div>
		);
	}

	// --- Payload'a normalize (backend buildTemplatePayload ile uyumlu) ---
	function buildTemplate() {
		const cmp = data.components;
		const base = { name: (data.name || '').trim().toLowerCase().replace(/\s+/g, '_'), language: data.language, category: data.category, allow_category_change: !!data.allow_category_change };

		if (isAuth) return { ...base, components: { auth: cmp.auth } };

		if (kind === 'CAROUSEL_PRODUCT') {
			return { ...base, components: { body: { text: cmp.body.text }, variables: cmp.variables, carousel: { enabled: true, cardType: 'PRODUCT', cardCount: _.toInteger(cmp.carousel.cardCount) || 2, buttonText: cmp.carousel.buttonText || 'View' } } };
		}
		if (kind === 'CAROUSEL_MEDIA') {
			const cards = cmp.carousel.cards.map((cd) => ({ url: cd.url, body: cd.body, buttons: cd.buttons || [] }));
			return { ...base, components: { body: { text: cmp.body.text }, variables: cmp.variables, carousel: { enabled: true, cardType: 'MEDIA', cardFormat: cmp.carousel.cardFormat, cards } } };
		}
		// STANDARD
		return {
			...base,
			components: {
				header: cmp.header.enabled ? cmp.header : { enabled: false },
				body: { text: cmp.body.text },
				footer: cmp.footer,
				buttons: cmp.buttons,
				variables: cmp.variables,
				lto: data.category === 'MARKETING' && cmp.lto.enabled ? { enabled: true, text: cmp.lto.text, has_expiration: cmp.lto.has_expiration } : { enabled: false },
				callPermission: cmp.callPermission.enabled ? { enabled: true } : { enabled: false },
			},
		};
	}

	function validate() {
		if (!data.name || !data.name.trim()) return t('templateNameRequired') || 'Şablon adı zorunlu';
		if (isAuth) {
			const a = data.components.auth;
			if ((a.otp_type === 'one_tap' || a.otp_type === 'zero_tap') && (!a.package_name || !a.signature_hash))
				return t('otpAppRequired') || 'One-tap/Zero-tap için package name ve signature hash zorunlu';
			return '';
		}
		if (!data.components.body.text || !data.components.body.text.trim()) return t('bodyTextRequired') || 'Gövde metni zorunlu';
		if (hasNamedVar && hasPositionalVar) return t('varMixError') || 'Named ve pozisyonel değişkenler aynı şablonda karıştırılamaz';
		if (bodyVariables.some((v) => !data.components.variables[v])) return t('variableExampleRequired') || 'Değişkenler için örnek değer girin';
		if (kind === 'STANDARD' && data.category === 'MARKETING' && data.components.lto.enabled && !data.components.lto.text.trim()) return t('offerTextRequired') || 'Teklif metni zorunlu';
		// Header text değişkeni varsa örnek zorunlu
		if (kind === 'STANDARD' && data.components.header.enabled && data.components.header.format === 'TEXT' && /{{\d+}}/.test(data.components.header.text) && !data.components.header.example)
			return t('headerExampleRequired') || 'Header değişkeni için örnek zorunlu';
		// URL butonu değişkeni varsa örnek zorunlu
		if (kind === 'STANDARD' && data.components.header.enabled && data.components.header.format !== 'TEXT' && isSvg(data.components.header.url))
			return t('svgNotSupported') || 'WhatsApp SVG desteklemez — JPEG/PNG görsel kullanın';
		if (kind === 'CAROUSEL_MEDIA') {
			const cards = data.components.carousel.cards;
			if (cards.some((c) => !c.url)) return t('cardMediaRequired') || 'Her kart için örnek medya URL gerekli';
			if (data.components.carousel.cardFormat === 'IMAGE' && cards.some((c) => isSvg(c.url))) return t('svgNotSupported') || 'WhatsApp SVG desteklemez — JPEG/PNG görsel kullanın';
			if (cards.some((c) => (c.buttons || []).some((b) => b.type === 'URL' && /{{\d+}}/.test(b.value || '') && !b.example))) return t('urlExampleRequired') || 'Dinamik URL için örnek zorunlu';
			// Meta kuralı: tüm kartlarda aynı sayıda ve aynı tip/sıra buton olmalı
			const sig = (c) => (c.buttons || []).map((b) => b.type).join(',');
			const first = sig(cards[0]);
			if (cards.some((c) => sig(c) !== first)) return t('cardButtonsMustMatch') || 'Tüm kartlarda aynı sayıda ve aynı tip butonlar olmalı';
		} else if (data.components.buttons.some((b) => b.type === 'URL' && /{{\d+}}/.test(b.value || '') && !b.example)) {
			return t('urlExampleRequired') || 'Dinamik URL için örnek zorunlu';
		}
		return '';
	}

	async function submit() {
		const v = validate();
		if (v) return setError(v);
		setError('');
		setLoading(true);
		try {
			const resp = await post('/dahi/channel/dialog360template/create', { waba_gen_id, template: buildTemplate() });
			if (resp && resp.data && resp.data.success) {
				onTemplateCreated(true);
				onClose();
			} else {
				setError((resp && resp.data && (resp.data.message || _.get(resp.data, 'error.error.error_user_msg') || _.get(resp.data, 'error.message'))) || 'Şablon oluşturulamadı');
			}
		} catch (e) {
			setError(e.message);
		} finally {
			setLoading(false);
		}
	}

	// --- Form parçaları ---
	function renderAuthForm() {
		const a = data.components.auth;
		const setAuth = (patch) => setComp('auth', patch);
		return (
			<Form>
				<Message info size="small">{t('authTemplateInfo') || 'Doğrulama (OTP) şablonu: gövde metni WhatsApp tarafından sabittir, sadece kod butonu ve ayarları girilir.'}</Message>
				<Form.Field>
					<Checkbox label={t('addSecurityRecommendation') || 'Güvenlik uyarısı ekle ("Kodu kimseyle paylaşmayın")'} checked={a.add_security_recommendation} onChange={(_, { checked }) => setAuth({ add_security_recommendation: checked })} />
				</Form.Field>
				<Form.Group widths="equal">
					<Form.Field>
						<label>{t('codeExpiration') || 'Kod geçerlilik (dk, 1-90)'}</label>
						<Input type="number" min={1} max={90} value={a.code_expiration_minutes} onChange={(e) => setAuth({ code_expiration_minutes: e.target.value })} />
					</Form.Field>
					<Form.Field>
						<label>OTP tipi</label>
						<Dropdown selection value={a.otp_type} options={[{ key: 'copy', text: 'Copy code', value: 'copy_code' }, { key: 'one', text: 'One tap', value: 'one_tap' }, { key: 'zero', text: 'Zero tap', value: 'zero_tap' }]} onChange={(_, { value }) => setAuth({ otp_type: value })} />
					</Form.Field>
				</Form.Group>
				<Form.Input label={t('buttonText') || 'Buton metni (ops.)'} value={a.button_text} onChange={(e) => setAuth({ button_text: e.target.value })} />
				{(a.otp_type === 'one_tap' || a.otp_type === 'zero_tap') && (
					<>
						<Form.Input label="Autofill text" value={a.autofill_text} onChange={(e) => setAuth({ autofill_text: e.target.value })} />
						<Form.Group widths="equal">
							<Form.Input label="Package name" value={a.package_name} onChange={(e) => setAuth({ package_name: e.target.value })} />
							<Form.Input label="Signature hash" value={a.signature_hash} onChange={(e) => setAuth({ signature_hash: e.target.value })} />
						</Form.Group>
					</>
				)}
			</Form>
		);
	}

	function renderBodyField() {
		const bodyLen = (data.components.body.text || '').length;
		return (
			<>
				<Form.Field>
					<div style={styles.sectionHeader}>
						<Label color="grey">BODY</Label>
						<Button.Group size="tiny" basic>
							{FORMATS.map((f) => (
								<Button key={f.icon} type="button" icon={f.icon} title={f.title} onClick={() => wrapSelection(f.marker)} />
							))}
						</Button.Group>
						<Dropdown icon={null} trigger={<Button type="button" size="tiny" basic icon="smile outline" />} pointing="top left">
							<Dropdown.Menu>
								<div style={{ display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: 2, padding: 6, fontSize: 18 }}>
									{EMOJIS.map((em) => (
										<span key={em} role="button" style={{ cursor: 'pointer', textAlign: 'center' }} onClick={() => insertAtCursor(em)}>{em}</span>
									))}
								</div>
							</Dropdown.Menu>
						</Dropdown>
						<Button type="button" size="tiny" content={t('addVariable') || 'Değişken Ekle'} onClick={addVariable} disabled={hasNamedVar} />
					</div>
					<textarea ref={bodyRef} rows={4} maxLength={LIMITS.body} style={{ width: '100%', padding: 9, borderRadius: 4, border: '1px solid rgba(34,36,38,.15)', fontFamily: 'inherit', resize: 'vertical' }} placeholder={t('typeBodyText') || 'Gövde metni'} value={data.components.body.text} onChange={(e) => setComp('body', { text: e.target.value })} />
					<div style={{ display: 'flex', justifyContent: 'space-between' }}>
						<div style={styles.hint}>{t('namedVarInfo') || 'İsimli değişken (ör. {{order_id}}) ekleyebilirsiniz. Named ve pozisyonel karıştırılamaz.'}</div>
						<div style={styles.hint}>{bodyLen} / {LIMITS.body}</div>
					</div>
					<div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
						<Input size="mini" placeholder={t('variableName') || 'değişken_adı'} value={namedInput} disabled={hasPositionalVar} onChange={(e) => setNamedInput(e.target.value)} action={{ size: 'mini', content: t('addNamedVariable') || 'İsimli ekle', onClick: addNamedVariable, disabled: hasPositionalVar }} />
					</div>
				</Form.Field>
				{bodyVariables.length > 0 && (
					<Form.Field>
						<Label color="grey">{t('exampleValues') || 'Örnek Değerler'}</Label>
						{bodyVariables.map((vv) => (
							<Form.Input key={vv} label={`{{${vv}}}`} value={data.components.variables[vv] || ''} onChange={(e) => setVariable(vv, e.target.value)} />
						))}
					</Form.Field>
				)}
			</>
		);
	}

	function renderStandardForm() {
		const h = data.components.header;
		const foot = data.components.footer;
		const lto = data.components.lto;
		const isMarketing = data.category === 'MARKETING';
		return (
			<Form>
				<div style={{ margin: '0 0 12px' }}>
					<Button type="button" size="small" basic={!h.enabled} color="teal" icon="image" content="Header" onClick={() => setComp('header', { enabled: !h.enabled })} />
					<Button type="button" size="small" basic={!foot.enabled} color="teal" icon="minus" content="Footer" onClick={() => setComp('footer', { enabled: !foot.enabled })} />
					{isMarketing && <Button type="button" size="small" basic={!lto.enabled} color="orange" icon="clock outline" content={t('limitedTimeOffer') || 'Limited Time Offer'} onClick={() => setComp('lto', { enabled: !lto.enabled })} />}
					<Button type="button" size="small" basic={!data.components.callPermission.enabled} color="purple" icon="phone" content={t('callPermission') || 'Call permission'} onClick={() => setComp('callPermission', { enabled: !data.components.callPermission.enabled })} />
				</div>

				{h.enabled && (
					<div style={styles.section}>
						<div style={styles.sectionHeader}>
							<Label color="blue">HEADER</Label>
							<Dropdown selection value={h.format} options={[{ key: 't', text: 'Text', value: 'TEXT' }, { key: 'i', text: 'Image', value: 'IMAGE' }, { key: 'v', text: 'Video', value: 'VIDEO' }, { key: 'd', text: 'Document', value: 'DOCUMENT' }]} onChange={(_, { value }) => setComp('header', { format: value })} />
						</div>
						{h.format === 'TEXT' ? (
							<>
								<Form.Input maxLength={LIMITS.header} placeholder={t('typeHeaderText') || 'Başlık metni ({{1}} olabilir)'} value={h.text} onChange={(e) => setComp('header', { text: e.target.value })} />
								<div style={{ ...styles.hint, textAlign: 'right' }}>{(h.text || '').length} / {LIMITS.header}</div>
								{/{{\d+}}/.test(h.text) && <Form.Input label={t('headerExample') || 'Header örnek değeri'} value={h.example} onChange={(e) => setComp('header', { example: e.target.value })} />}
							</>
						) : (
							<Form.Field>
								<label>{t('sampleMediaUrl') || 'Örnek medya (onay için)'}</label>
								{h.format === 'IMAGE' && h.url && !isSvg(h.url) && <img src={h.url} alt="" style={styles.thumb} onError={(e) => { e.target.style.display = 'none'; }} />}
								<MediaInput accept={acceptFor(h.format)} value={h.url} onChange={(url) => setComp('header', { url })} />
							</Form.Field>
						)}
					</div>
				)}

				<div style={styles.section}>{renderBodyField()}</div>

				{isMarketing && lto.enabled && (
					<div style={styles.section}>
						<div style={styles.sectionHeader}><Label color="orange">LIMITED TIME OFFER</Label></div>
						<Message info size="small">{t('ltoInfo') || 'Kampanya/geri sayım teklifi. Kupon kodu (COPY_CODE) ve/veya URL butonu ekleyin. Bitiş zamanı gönderimde verilir.'}</Message>
						<Form.Input maxLength={16} label={t('offerText') || 'Teklif metni'} placeholder={t('offerTextPh') || '%20 indirim'} value={lto.text} onChange={(e) => setComp('lto', { text: e.target.value })} />
						<Form.Field>
							<Checkbox toggle label={t('hasExpiration') || 'Bitiş zamanı göster (geri sayım)'} checked={lto.has_expiration} onChange={(_, { checked }) => setComp('lto', { has_expiration: checked })} />
						</Form.Field>
					</div>
				)}

				{data.components.callPermission.enabled && (
					<div style={styles.section}>
						<div style={styles.sectionHeader}><Label color="purple">CALL PERMISSION</Label></div>
						<Message info size="small">{t('callPermissionInfo') || 'Sesli arama izni isteği bileşeni eklenir. Kullanıcıya arama izni sorulur; ek alan gerekmez.'}</Message>
					</div>
				)}

				{foot.enabled && (
					<div style={styles.section}>
						<div style={styles.sectionHeader}><Label color="grey">FOOTER</Label></div>
						<Form.Input maxLength={LIMITS.footer} value={foot.text} onChange={(e) => setComp('footer', { text: e.target.value })} />
						<div style={{ ...styles.hint, textAlign: 'right' }}>{(foot.text || '').length} / {LIMITS.footer}</div>
					</div>
				)}

				<div style={styles.section}>
					<div style={styles.sectionHeader}><Label color="grey">BUTTONS</Label></div>
					{renderButtons(data.components.buttons, (buttons) => setData((p) => ({ ...p, components: { ...p.components, buttons } })))}
				</div>
			</Form>
		);
	}

	function renderProductCarousel() {
		const car = data.components.carousel;
		return (
			<Form>
				<Message info size="small">{t('productCarouselInfo') || 'Ürünler gönderim anında (product_retailer_id) belirlenir. Onay için 2-10 kart iskeleti oluşturulur.'}</Message>
				{renderBodyField()}
				<Form.Group widths="equal">
					<Form.Field>
						<label>{t('cardCount') || 'Kart Sayısı (2-10)'}</label>
						<Input type="number" min={2} max={10} value={car.cardCount} onChange={(e) => setComp('carousel', { cardCount: e.target.value })} />
					</Form.Field>
					<Form.Field>
						<label>{t('buttonText') || 'Buton Metni'}</label>
						<Input value={car.buttonText} onChange={(e) => setComp('carousel', { buttonText: e.target.value })} />
					</Form.Field>
				</Form.Group>
			</Form>
		);
	}

	function renderMediaCarousel() {
		const car = data.components.carousel;
		const setCard = (i, patch) => setComp('carousel', { cards: car.cards.map((c, idx) => (idx === i ? { ...c, ...patch } : c)) });
		return (
			<Form>
				<Message info size="small">{t('mediaCarouselInfo') || 'Her kartın kendi görseli, metni ve linki olur. Kural: tüm kartlarda aynı medya tipi ve aynı sayıda/tipte buton olmalı. En az 2 kart.'}</Message>
				{renderBodyField()}
				<Form.Group widths="equal">
					<Form.Field>
						<label>{t('cardMediaType') || 'Kart Medya Tipi'}</label>
						<Dropdown selection value={car.cardFormat} options={[{ key: 'i', text: 'Image', value: 'IMAGE' }, { key: 'v', text: 'Video', value: 'VIDEO' }]} onChange={(_, { value }) => setComp('carousel', { cardFormat: value })} />
					</Form.Field>
					<Form.Field>
						<label>&nbsp;</label>
						<Button type="button" icon="add" content={t('addCard') || 'Kart Ekle'} disabled={car.cards.length >= 10} onClick={() => setComp('carousel', { cards: [...car.cards, { url: '', body: '', buttons: [] }] })} />
					</Form.Field>
				</Form.Group>
				{car.cards.map((cd, i) => (
					<div key={i} style={styles.card}>
						<div style={styles.cardHead}>
							<Label circular color="teal" size="small">{i + 1}</Label>
							<span style={{ flex: 1, marginLeft: 8, fontWeight: 600, color: '#5a616b' }}>{t('card') || 'Kart'} {i + 1}</span>
							{car.cards.length > 2 && <Button type="button" icon="trash" size="tiny" basic color="red" onClick={() => setComp('carousel', { cards: car.cards.filter((_, idx) => idx !== i) })} />}
						</div>
						{car.cardFormat === 'IMAGE' && cd.url && !isSvg(cd.url) ? (
							<img src={cd.url} alt="" style={styles.thumb} onError={(e) => { e.target.style.display = 'none'; }} />
						) : (
							<div style={styles.thumbPlaceholder}><Icon name={car.cardFormat === 'VIDEO' ? 'video' : 'image outline'} size="large" /></div>
						)}
						<Form.Field>
							<label>{t('sampleMediaUrl') || 'Görsel/Video'}</label>
							<MediaInput accept={acceptFor(car.cardFormat)} value={cd.url} onChange={(url) => setCard(i, { url })} />
						</Form.Field>
						<Form.Input label={t('cardBody') || 'Kart metni'} value={cd.body} onChange={(e) => setCard(i, { body: e.target.value })} />
						<Label color="grey" size="tiny">{t('cardButtons') || 'Kart butonları (link vs.)'}</Label>
						{renderButtons(cd.buttons || [], (buttons) => setCard(i, { buttons }), true)}
					</div>
				))}
			</Form>
		);
	}

	function renderPreview() {
		const { header, body, footer, buttons, carousel, auth, lto, callPermission } = data.components;
		const bodyText = (body.text || '').replace(VAR_RE, (m, tok) => data.components.variables[tok] || m);
		const headerImg = kind === 'STANDARD' && header.enabled && header.format !== 'TEXT' && header.url && !isSvg(header.url) ? header.url : null;
		const headerText = kind === 'STANDARD' && header.enabled && header.format === 'TEXT' ? (header.text || '').replace(/{{(\d+)}}/g, (m, n) => header.example || m) : null;
		return (
			<div style={styles.phone}>
				<div style={styles.bubble}>
					{isAuth ? (
						<>
							<div style={styles.previewBody}>{t('authPreviewBody') || '<Kod> tek seferlik doğrulama kodunuz.'}{auth.add_security_recommendation ? ' ' + (t('authSecurity') || 'Bu kodu kimseyle paylaşmayın.') : ''}</div>
							{auth.code_expiration_minutes ? <div style={styles.previewFooter}>{t('expiresIn') || 'Geçerlilik'}: {auth.code_expiration_minutes} dk</div> : null}
							<div style={styles.previewButton}><Icon name="copy outline" /> {auth.button_text || (t('copyCode') || 'Kodu kopyala')}</div>
						</>
					) : (
						<>
							{headerImg && <img src={headerImg} alt="" style={styles.previewMedia} onError={(e) => { e.target.style.display = 'none'; }} />}
							{kind === 'STANDARD' && header.enabled && header.format === 'VIDEO' && <div style={styles.previewMediaPh}><Icon name="video" size="big" /></div>}
							{kind === 'STANDARD' && header.enabled && header.format === 'DOCUMENT' && <div style={styles.previewMediaPh}><Icon name="file outline" size="big" /></div>}
							{headerText && <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>{headerText}</div>}
							{kind === 'STANDARD' && data.category === 'MARKETING' && lto.enabled && (
								<div style={{ background: '#fff3e0', color: '#e65100', borderRadius: 6, padding: '4px 8px', fontSize: 12, marginBottom: 6, display: 'flex', justifyContent: 'space-between' }}>
									<span><Icon name="clock outline" /> {lto.text || (t('offerText') || 'Teklif')}</span>
									{lto.has_expiration && <span>⏳ 24:00:00</span>}
								</div>
							)}
							<div style={styles.previewBody}>{bodyText || (t('prewiewMessage') || 'Mesaj önizlemesi')}</div>
							{kind === 'STANDARD' && footer.enabled && footer.text && <div style={styles.previewFooter}>{footer.text}</div>}
							{kind === 'STANDARD' && buttons.map((b, i) => <div key={i} style={styles.previewButton}>{b.text || b.type}</div>)}
							{kind === 'STANDARD' && callPermission.enabled && <div style={styles.previewButton}><Icon name="phone" /> {t('allowCall') || 'Aramaya izin ver'}</div>}
						</>
					)}
				</div>
				{!isAuth && kind === 'CAROUSEL_PRODUCT' && (
					<div style={styles.carouselStrip}>
						{_.times(_.clamp(_.toInteger(carousel.cardCount) || 2, 2, 10), (i) => (
							<div key={i} style={styles.carouselItem}>
								<div style={{ ...styles.previewMediaPh, height: 100, marginBottom: 0 }}><Icon name="box" size="big" /></div>
								<div style={{ padding: '4px 6px', fontSize: 12, color: '#888' }}>{t('product') || 'Ürün'} {i + 1}</div>
								<div style={{ ...styles.previewButton, marginTop: 0 }}>{carousel.buttonText || 'View'}</div>
							</div>
						))}
					</div>
				)}
				{!isAuth && kind === 'CAROUSEL_MEDIA' && (
					<div style={styles.carouselStrip}>
						{carousel.cards.map((cd, i) => (
							<div key={i} style={styles.carouselItem}>
								{carousel.cardFormat === 'IMAGE' && cd.url && !isSvg(cd.url) ? (
									<img src={cd.url} alt="" style={{ ...styles.previewMedia, marginBottom: 0, maxHeight: 100 }} onError={(e) => { e.target.style.display = 'none'; }} />
								) : (
									<div style={{ ...styles.previewMediaPh, height: 100, marginBottom: 0 }}><Icon name={carousel.cardFormat === 'VIDEO' ? 'video' : 'image outline'} size="big" /></div>
								)}
								<div style={{ padding: '6px', fontSize: 12 }}>{cd.body}</div>
								{(cd.buttons || []).map((b, bi) => <div key={bi} style={{ ...styles.previewButton, marginTop: 0 }}>{b.text || b.type}</div>)}
							</div>
						))}
					</div>
				)}
			</div>
		);
	}

	return (
		<Modal open={modalOpen} onClose={onClose} size="large">
			<Modal.Header>{t('newTemplate') || 'Yeni Şablon'}</Modal.Header>
			<Modal.Content scrolling>
				{error && <Message negative><p>{error}</p></Message>}
				<Form style={{ marginBottom: 10 }}>
					<Form.Field>
						<label>{t('templateName') || 'Şablon Adı'}</label>
						<Input placeholder={t('enterTemplateName') || 'ornek_sablon_adi'} value={data.name} onChange={(e) => setData({ ...data, name: e.target.value })} />
					</Form.Field>
					<Form.Group widths="equal">
						<Form.Field>
							<label>{t('category') || 'Kategori'}</label>
							<Dropdown selection value={data.category} options={[{ key: 'm', text: 'Marketing', value: 'MARKETING' }, { key: 'u', text: 'Utility', value: 'UTILITY' }, { key: 'a', text: 'Authentication', value: 'AUTHENTICATION' }]} onChange={(_, { value }) => setData({ ...data, category: value })} />
						</Form.Field>
						{!isAuth && (
							<Form.Field>
								<label>{t('templateType') || 'Şablon Tipi'}</label>
								<Dropdown selection value={data.templateKind} options={[{ key: 's', text: t('standardTemplate') || 'Standart', value: 'STANDARD' }, { key: 'cp', text: t('productCarousel') || 'Ürün Carousel', value: 'CAROUSEL_PRODUCT' }, { key: 'cm', text: t('mediaCarousel') || 'Medya Carousel', value: 'CAROUSEL_MEDIA' }]} onChange={(_, { value }) => setData({ ...data, templateKind: value })} />
							</Form.Field>
						)}
						<Form.Field>
							<label>{t('language') || 'Dil'}</label>
							<Dropdown selection search value={data.language} options={LANGUAGE_OPTIONS} onChange={(_, { value }) => setData({ ...data, language: value })} />
						</Form.Field>
					</Form.Group>
					{!isAuth && (
						<Form.Field>
							<Checkbox label={t('allowCategoryChange') || "Meta kategoriyi değiştirebilsin (allow category change)"} checked={data.allow_category_change} onChange={(_, { checked }) => setData({ ...data, allow_category_change: checked })} />
						</Form.Field>
					)}
				</Form>

				<div style={{ display: 'flex', gap: 20 }}>
					<div style={{ flex: 2 }}>
						{isAuth ? renderAuthForm() : kind === 'CAROUSEL_PRODUCT' ? renderProductCarousel() : kind === 'CAROUSEL_MEDIA' ? renderMediaCarousel() : renderStandardForm()}
					</div>
					<div style={{ flex: 1 }}>
						<h4>{t('preview') || 'Önizleme'}</h4>
						{renderPreview()}
					</div>
				</div>
			</Modal.Content>
			<Modal.Actions>
				<Button onClick={onClose}>{t('cancel') || 'İptal'}</Button>
				<Button primary loading={loading} onClick={submit}>{t('submitTemplate') || 'Onaya Gönder'}</Button>
			</Modal.Actions>
		</Modal>
	);
}

export default connect()(withTranslation()(CreateDialog360Template));
